import { api } from './client';

export interface DFeManifestacao {
  id: number;
  ambiente?: string | null;
  chave_acesso?: string | null;
  tipo_evento: string;
  status?: string | null;
  justificativa?: string | null;
  codigo_status?: string | null;
  motivo_status?: string | null;
  numero_protocolo?: string | null;
  data_evento?: string | null;
  criado_em?: string | null;
  xml_baixado?: boolean;
  xml_mensagem?: string | null;
  documento?: DFeDocumento;
}

export interface DFeTrilhaStep {
  label: string;
  status: 'done' | 'current' | 'pending';
}

export interface DFeHistoricoItem {
  tipo: string;
  titulo: string;
  data?: string | null;
  cstat?: string | null;
  protocolo?: string | null;
  motivo?: string | null;
  sucesso?: boolean;
  manifestacao?: DFeManifestacao;
}

export interface DFeDocumento {
  id: number;
  nsu?: string | null;
  schema?: string | null;
  tipo_documento: string;
  chave_acesso?: string | null;
  numero?: string | null;
  serie?: string | null;
  valor_nfe?: number;
  emitente_cpf_cnpj?: string | null;
  emitente_nome?: string | null;
  data_evento?: string | null;
  data_recebimento?: string | null;
  tem_xml: boolean;
  tem_xml_completo?: boolean;
  is_resumo?: boolean;
  precisa_manifestacao?: boolean;
  pode_danfe?: boolean;
  pode_devolucao?: boolean;
  pode_importar_compra?: boolean;
  xml_status?: 'completo' | 'resumo' | 'indisponivel' | 'invalido' | string;
  importado_em?: string | null;
  compra_id?: number | null;
  rascunho_nfe_id?: number | null;
  nota_concluida?: boolean;
  status: string;
  manifestacoes_count?: number;
  ultima_manifestacao?: DFeManifestacao | null;
  trilha?: DFeTrilhaStep[];
}

export interface DFeDocumentoDetalhe extends DFeDocumento {
  manifestacoes: DFeManifestacao[];
  historico: DFeHistoricoItem[];
}

export interface DFeListResponse {
  data: DFeDocumento[];
  meta: { ultimo_nsu: string };
}

export async function listDFeDocumentos(emitenteId: number, q?: string): Promise<DFeListResponse> {
  const { data } = await api.get<DFeListResponse>('/dfe/documentos', {
    params: { emitente_id: emitenteId, q: q || undefined },
  });
  return data;
}

export async function getDFeDocumento(emitenteId: number, docId: number): Promise<DFeDocumentoDetalhe> {
  const { data } = await api.get<DFeDocumentoDetalhe>(`/dfe/documentos/${docId}`, {
    params: { emitente_id: emitenteId },
  });
  return data;
}

export async function getDFeHistorico(
  emitenteId: number,
  docId: number,
): Promise<{ documento: DFeDocumento; manifestacoes: DFeManifestacao[]; historico: DFeHistoricoItem[] }> {
  const { data } = await api.get(`/dfe/documentos/${docId}/historico`, {
    params: { emitente_id: emitenteId },
  });
  return data;
}

export async function consultarDFe(emitenteId: number): Promise<{
  documentos_novos: number;
  total_recebido: number;
  ultimo_nsu: string;
  max_nsu?: string;
  mensagem?: string;
}> {
  const { data } = await api.post('/dfe/documentos/consultar', {
    emitente_id: emitenteId,
    tipo_consulta: 'dist-nsu',
  });
  return data;
}

export async function setDFeNsu(emitenteId: number, ultimoNsu: string): Promise<{ ultimo_nsu: string }> {
  const { data } = await api.put('/dfe/nsu', { emitente_id: emitenteId, ultimo_nsu: ultimoNsu });
  return data;
}

export async function manifestarDFe(
  emitenteId: number,
  docId: number,
  tipoEvento: string,
  justificativa?: string,
): Promise<DFeManifestacao> {
  const { data } = await api.post(`/dfe/documentos/${docId}/manifestar`, {
    emitente_id: emitenteId,
    tipo_evento: tipoEvento,
    justificativa: justificativa || undefined,
  });
  return data;
}

export async function buscarXmlCompletoDFe(
  emitenteId: number,
  docId: number,
): Promise<{ ok: boolean; mensagem?: string; detail?: string; documento: DFeDocumento }> {
  const { data } = await api.post(`/dfe/documentos/${docId}/xml/completo`, { emitente_id: emitenteId });
  return data;
}

export async function criarDevolucaoDFe(
  emitenteId: number,
  docId: number,
): Promise<{ rascunho_id: number; reutilizado: boolean }> {
  const { data } = await api.post(`/dfe/documentos/${docId}/devolucao`, { emitente_id: emitenteId });
  return data;
}

export async function importarCompraDFe(
  emitenteId: number,
  docId: number,
): Promise<{ compra_id: number; reutilizado: boolean }> {
  const { data } = await api.post(`/dfe/documentos/${docId}/importar-compra`, { emitente_id: emitenteId });
  return data;
}

export async function concluirDFe(
  emitenteId: number,
  docId: number,
  notaConcluida: boolean,
): Promise<DFeDocumento> {
  const { data } = await api.put(`/dfe/documentos/${docId}/concluir`, {
    emitente_id: emitenteId,
    nota_concluida: notaConcluida,
  });
  return data;
}
